import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './hooks/useAuth';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import GATracker from './components/GATracker';
import PageTransition from './components/PageTransition';

// Lazy-loaded pages (code splitting — only loaded when route is visited)
const HomePage = lazy(() => import('./pages/Home'));
const RegisterPage = lazy(() => import('./pages/Auth').then(m => ({ default: m.RegisterPage })));
const LoginPage = lazy(() => import('./pages/Auth').then(m => ({ default: m.LoginPage })));
const VerifyOTPPage = lazy(() => import('./pages/Auth').then(m => ({ default: m.VerifyOTPPage })));
const ForgotPasswordPage = lazy(() => import('./pages/Auth').then(m => ({ default: m.ForgotPasswordPage })));
const DashboardLayout = lazy(() => import('./components/DashboardLayout'));
const Overview = lazy(() => import('./pages/dashboard/Overview'));
const LearningPage = lazy(() => import('./pages/dashboard/Learning'));
const QuizPage = lazy(() => import('./pages/dashboard/Quiz'));
const ProjectPage = lazy(() => import('./pages/dashboard/Project'));
const CertificatesPage = lazy(() => import('./pages/dashboard/Certificates'));
const PaymentsPage = lazy(() => import('./pages/dashboard/Payments'));
const WalletReferralsPage = lazy(() => import('./pages/dashboard/WalletReferrals'));
const GamificationPage = lazy(() => import('./pages/dashboard/Gamification'));
const OfferLettersPage = lazy(() => import('./pages/dashboard/OfferLetters'));
const ProfilePage = lazy(() => import('./pages/dashboard/Profile'));
const OnboardingWizard = lazy(() => import('./components/onboarding/OnboardingWizard'));
const InternshipsPage = lazy(() => import('./pages/Internships').then(m => ({ default: m.InternshipsPage })));
const InternshipDetailPage = lazy(() => import('./pages/Internships').then(m => ({ default: m.InternshipDetailPage })));
const VerifyPage = lazy(() => import('./pages/Verify').then(m => ({ default: m.VerifyPage })));
const VerifyOfferLetterPage = lazy(() => import('./pages/VerifyOfferLetter'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const AdminPanel = lazy(() => import('./pages/Admin'));
const AboutPage = lazy(() => import('./pages/StaticPages').then(m => ({ default: m.AboutPage })));
const PrivacyPage = lazy(() => import('./pages/StaticPages').then(m => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import('./pages/StaticPages').then(m => ({ default: m.TermsPage })));
const RefundPage = lazy(() => import('./pages/StaticPages').then(m => ({ default: m.RefundPage })));
const DisclaimerPage = lazy(() => import('./pages/StaticPages').then(m => ({ default: m.DisclaimerPage })));
const ContactPage = lazy(() => import('./pages/StaticPages').then(m => ({ default: m.ContactPage })));
const HelpCenterPage = lazy(() => import('./pages/HelpCenter'));

import './styles/globals.css';
import './styles/employee.css';
import 'highlight.js/styles/github-dark.css';

// ─── Employee Portal (lazy) ──────────────────────────────────
const { EmployeeProvider, useEmployee } = require('./hooks/useEmployee');
const EmployeeLogin = lazy(() => import('./pages/employee/EmployeeLogin'));
const ForcePasswordChange = lazy(() => import('./pages/employee/ForcePasswordChange'));
const EmployeeLayout = lazy(() => import('./pages/employee/EmployeeLayout'));
const EmployeeDashboard = lazy(() => import('./pages/employee/EmployeeDashboard'));
const EmployeeTasks = lazy(() => import('./pages/employee/EmployeeTasks'));
const EmployeeProfile = lazy(() => import('./pages/employee/EmployeeProfile'));
const EmployeeTaskDetail = lazy(() => import('./pages/employee/EmployeeTaskDetail'));
const EmployeeNotifications = lazy(() => import('./pages/employee/EmployeeNotifications'));
const EmployeeAnnouncements = lazy(() => import('./pages/employee/EmployeeAnnouncements'));
const EmployeeEmployees = lazy(() => import('./pages/employee/EmployeeEmployees'));
const EmployeeSettings = lazy(() => import('./pages/employee/EmployeeSettings'));

// ─── Cookie / Privacy Consent Banner ────────────────────────
// Shown once per browser. Stored in localStorage (not a cookie,
// so GDPR-technically this consent is for data processing, not cookie
// storage — but covers India's DPDP consent requirement as well).
const CONSENT_KEY = 'getnextern_privacy_consent';
const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(CONSENT_KEY)) setVisible(true);
  }, []);

  if (!visible) return null;

  const accept = () => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ accepted: true, ts: Date.now() }));
    setVisible(false);
  };

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
      background: '#0f172a', color: '#f8fafc',
      padding: '16px 24px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 16, flexWrap: 'wrap',
      boxShadow: '0 -4px 24px rgba(0,0,0,0.2)',
    }}>
      <p style={{ margin: 0, fontSize: 14, color: '#cbd5e1', flex: 1, minWidth: 240 }}>
        We use essential data to run GetNextern — authentication, payments, and certificates.
        By continuing you agree to our{' '}
        <a href="/privacy" style={{ color: '#60a5fa', textDecoration: 'underline' }}>Privacy Policy</a>
        {' '}and{' '}
        <a href="/terms" style={{ color: '#60a5fa', textDecoration: 'underline' }}>Terms of Service</a>.
      </p>
      <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
        <a href="/privacy" style={{ fontSize: 13, color: '#94a3b8', textDecoration: 'underline', alignSelf: 'center' }}>
          Learn more
        </a>
        <button
          onClick={accept}
          style={{
            background: '#2563eb', color: '#fff', border: 'none',
            borderRadius: 8, padding: '9px 20px', fontSize: 14,
            fontWeight: 600, cursor: 'pointer',
          }}
        >
          Accept &amp; Continue
        </button>
      </div>
    </div>
  );
};

// ─── Protected Student Route ─────────────────────────────────
const StudentRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid #e2e8f0', borderTopColor: '#2563eb', animation: 'spin 0.6s linear infinite' }} />
    </div>
  );

  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (user.role === 'admin') return <Navigate to="/admin" replace />;
  return children;
};

// ─── Protected Admin Route ───────────────────────────────────
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid #e2e8f0', borderTopColor: '#2563eb', animation: 'spin 0.6s linear infinite' }} />
    </div>
  );

  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
};

// ─── Public only (redirect if logged in) ────────────────────
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={user.role === 'admin' ? '/admin' : '/'} replace />;
  return children;
};

// ─── Protected Employee Route ───────────────────────────────
const EmployeeRoute = ({ children }) => {
  const { employee, loading } = useEmployee();
  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid #e2e8f0', borderTopColor: '#7C3AED', animation: 'spin 0.6s linear infinite' }} />
    </div>
  );
  if (!employee) return <Navigate to="/employee/login" replace />;
  return children;
};

// ─── Layout wrapper ──────────────────────────────────────────
const WithLayout = ({ children, noFooter = false }) => (
  <>
    <Navbar />
    <PageTransition>{children}</PageTransition>
    {!noFooter && <Footer />}
  </>
);

// ─── App ─────────────────────────────────────────────────────
const PageLoader = () => (
  <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid #e2e8f0', borderTopColor: '#2563eb', animation: 'spin 0.6s linear infinite' }} />
  </div>
);

const AppRoutes = () => (
  <Suspense fallback={<PageLoader />}>
  <Routes>
    {/* Public with layout */}
    <Route path="/" element={<WithLayout><HomePage /></WithLayout>} />
    <Route path="/internships" element={<WithLayout><InternshipsPage /></WithLayout>} />
    <Route path="/internships/:slug" element={<WithLayout><InternshipDetailPage /></WithLayout>} />
    <Route path="/verify" element={<WithLayout><VerifyPage /></WithLayout>} />
    <Route path="/verify/:certId" element={<WithLayout><VerifyPage /></WithLayout>} />
    <Route path="/verify/offer/:offerLetterId" element={<WithLayout><VerifyOfferLetterPage /></WithLayout>} />
    <Route path="/about" element={<WithLayout><AboutPage /></WithLayout>} />
    <Route path="/privacy" element={<WithLayout><PrivacyPage /></WithLayout>} />
    <Route path="/terms" element={<WithLayout><TermsPage /></WithLayout>} />
    <Route path="/refund" element={<WithLayout><RefundPage /></WithLayout>} />
    <Route path="/disclaimer" element={<WithLayout><DisclaimerPage /></WithLayout>} />
    <Route path="/contact" element={<WithLayout><ContactPage /></WithLayout>} />
    <Route path="/help" element={<WithLayout><HelpCenterPage /></WithLayout>} />

    {/* Auth pages (public only, no footer on auth pages) */}
    <Route path="/register" element={<PublicRoute><WithLayout noFooter><RegisterPage /></WithLayout></PublicRoute>} />
    <Route path="/login" element={<PublicRoute><WithLayout noFooter><LoginPage /></WithLayout></PublicRoute>} />
    <Route path="/verify-otp" element={<WithLayout noFooter><VerifyOTPPage /></WithLayout>} />
    <Route path="/forgot-password" element={<PublicRoute><WithLayout noFooter><ForgotPasswordPage /></WithLayout></PublicRoute>} />
    <Route path="/auth/callback" element={<AuthCallback />} />

    {/* Student dashboard */}
    <Route path="/dashboard/*" element={<StudentRoute><WithLayout noFooter><DashboardLayout /></WithLayout></StudentRoute>}>
      <Route index element={<Overview />} />
      <Route path="learning" element={<LearningPage />} />
      <Route path="quiz" element={<QuizPage />} />
      <Route path="project" element={<ProjectPage />} />
      <Route path="certificates" element={<CertificatesPage />} />
      <Route path="payments" element={<PaymentsPage />} />
      <Route path="wallet-referrals" element={<WalletReferralsPage />} />
      <Route path="gamification" element={<GamificationPage />} />
      <Route path="offer-letters" element={<OfferLettersPage />} />
      <Route path="profile" element={<ProfilePage />} />
    </Route>

    {/* Welcome celebration (shown once after registration) */}
    <Route path="/welcome" element={<StudentRoute><OnboardingWizard /></StudentRoute>} />

    {/* Admin panel (no public navbar/footer) */}
    <Route path="/admin/*" element={<AdminRoute><WithLayout noFooter><AdminPanel /></WithLayout></AdminRoute>} />

    {/* Employee Portal (no navbar/footer, own layout) */}
    <Route path="/employee/login" element={<EmployeeProvider><WithLayout noFooter><EmployeeLogin /></WithLayout></EmployeeProvider>} />
    <Route path="/employee/force-password" element={<EmployeeProvider><EmployeeRoute><ForcePasswordChange /></EmployeeRoute></EmployeeProvider>} />
    <Route path="/employee" element={<EmployeeProvider><EmployeeRoute><EmployeeLayout /></EmployeeRoute></EmployeeProvider>}>
      <Route index element={<Navigate to="dashboard" replace />} />
      <Route path="dashboard" element={<EmployeeDashboard />} />
      <Route path="tasks" element={<EmployeeTasks />} />
      <Route path="tasks/:id" element={<EmployeeTaskDetail />} />
      <Route path="profile" element={<EmployeeProfile />} />
      <Route path="employees" element={<EmployeeEmployees />} />
      <Route path="notifications" element={<EmployeeNotifications />} />
      <Route path="announcements" element={<EmployeeAnnouncements />} />
      <Route path="settings" element={<EmployeeSettings />} />
    </Route>

    {/* 404 */}
    <Route path="*" element={
      <WithLayout>
        <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', paddingTop: 88 }}>
          <div>
            <div style={{ fontSize: 80, fontWeight: 900, color: '#e2e8f0', lineHeight: 1 }}>404</div>
            <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 12 }}>Page Not Found</h1>
            <p style={{ color: '#64748b', marginBottom: 28 }}>The page you're looking for doesn't exist.</p>
            <a href="/" className="btn btn-primary">Go Home</a>
          </div>
        </div>
      </WithLayout>
    } />
  </Routes>
  </Suspense>
);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <GATracker />
        <AuthProvider>
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
          <CookieConsentBanner />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#fff',
                color: '#0f172a',
                border: '1.5px solid #e2e8f0',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                fontSize: '14px',
                fontWeight: '500',
                padding: '14px 18px',
              },
              success: {
                iconTheme: { primary: '#16a34a', secondary: '#fff' },
              },
              error: {
                iconTheme: { primary: '#dc2626', secondary: '#fff' },
              },
            }}
          />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}